import React, { useState, useMemo } from "react";
import resStyles from "../css/scanningSessionResponsive.module.scss";
import { formatTime24Hour } from "common/service/CommonService";

interface MobileScansOverlayProps {
  historyScans: any[];
  activeTab: "select" | "step1" | "step2";
  setIsShowingAllScans: (show: boolean) => void;
}

const MobileScanningScansOverlay: React.FC<MobileScansOverlayProps> = ({
  historyScans,
  activeTab,
  setIsShowingAllScans,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<"ALL" | "VALID" | "INVALID">("ALL");

  // Calculate status counts based on historyScans
  const counts = useMemo(() => {
    let valid = 0;
    let invalid = 0;
    historyScans.forEach((scan) => {
      const status = (scan.result || "valid").toLowerCase();
      if (status === "valid") {
        valid++;
      } else {
        invalid++;
      }
    });
    return {
      all: historyScans.length,
      valid,
      invalid,
    };
  }, [historyScans]);

  // Filter historyScans based on search term and status filter
  const filteredScans = useMemo(() => {
    return historyScans.filter((scan) => {
      // 1. Status Filter
      const status = (scan.result || "valid").toLowerCase();
      if (statusFilter === "VALID" && status !== "valid") return false;
      if (statusFilter === "INVALID" && status === "valid") return false;

      // 2. Search Term Filter (checks barcodes and result message)
      if (searchTerm.trim() !== "") {
        const query = searchTerm.toLowerCase();
        const subject = (scan.subjectbarcode || "").toLowerCase();
        const vacutainer = (scan.vaccutainerbarcode || "").toLowerCase();
        const aliquot = (scan.aliquotbarcode || "").toLowerCase();
        const msg = (scan.resultmessage || "").toLowerCase();

        return (
          subject.includes(query) ||
          vacutainer.includes(query) ||
          aliquot.includes(query) ||
          msg.includes(query)
        );
      }

      return true;
    });
  }, [historyScans, statusFilter, searchTerm]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Close only if clicking directly on the backdrop (not inside the modal content)
    if (e.target === e.currentTarget) {
      setIsShowingAllScans(false);
    }
  };

  return (
    <div className={resStyles.overlayBackdrop} onClick={handleBackdropClick}>
      <div className={resStyles.overlayContent}>
        {/* Header */}
        <div className={resStyles.overlayHeader}>
          <h5 className={resStyles.overlayTitle}>
            <i className="ri-history-line"></i> All Session Scans
          </h5>
          <button
            type="button"
            className={resStyles.closeOverlayBtn}
            onClick={() => setIsShowingAllScans(false)}
            aria-label="Close scans overview"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Search and Filters */}
        <div className={resStyles.overlayControls}>
          {/* Search Box */}
          <div className={resStyles.searchBox}>
            <i className="ri-search-line"></i>
            <input
              type="text"
              placeholder="Search by barcode or message..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                className={resStyles.clearSearchBtn}
                onClick={() => setSearchTerm("")}
              >
                <i className="ri-close-circle-fill"></i>
              </button>
            )}
          </div>

          {/* Status Filter Chips */}
          <div className={resStyles.filterChips}>
            <button
              type="button"
              className={`${resStyles.filterChip} ${statusFilter === "ALL" ? resStyles.active : ""}`}
              onClick={() => setStatusFilter("ALL")}
            >
              All Scans
              <span className={`${resStyles.chipCount} ${statusFilter === "ALL" ? resStyles.activeCount : ""}`}>
                {counts.all}
              </span>
            </button>
            <button
              type="button"
              className={`${resStyles.filterChip} ${statusFilter === "VALID" ? resStyles.active : ""}`}
              onClick={() => setStatusFilter("VALID")}
            >
              <i className="ri-checkbox-circle-line" style={{ color: "#16a34a" }}></i>
              Valid
              <span className={`${resStyles.chipCount} ${statusFilter === "VALID" ? resStyles.activeCount : ""}`}>
                {counts.valid}
              </span>
            </button>
            <button
              type="button"
              className={`${resStyles.filterChip} ${statusFilter === "INVALID" ? resStyles.active : ""}`}
              onClick={() => setStatusFilter("INVALID")}
            >
              <i className="ri-error-warning-line" style={{ color: "#ef4444" }}></i>
              Invalid
              <span className={`${resStyles.chipCount} ${statusFilter === "INVALID" ? resStyles.activeCount : ""}`}>
                {counts.invalid}
              </span>
            </button>
          </div>
        </div>

        {/* Scrollable Scans List */}
        <div className={resStyles.overlayScansList}>
          {filteredScans.length === 0 ? (
            <div className={resStyles.emptyStateContainer}>
              <i className={`ri-search-2-line ${resStyles.emptyIcon}`}></i>
              <h6>No Matching Scans Found</h6>
              <p>Try adjusting your search query or filter settings.</p>
            </div>
          ) : (
            filteredScans.map((row: any, idx: number) => {
              const dateStr = row.scandate
                ? new Date(row.scandate).toLocaleDateString([], {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
                : "-";
              const timeStr = row.scandate
                ? new Date(row.scandate).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: false,
                  })
                : "-";
              const resultStatus = (row.result || "valid").toLowerCase();
              const isScanValid = resultStatus === "valid";
              const isScanSkipped = resultStatus === "skipped";

              const cardClass = isScanValid ? resStyles.validScan : isScanSkipped ? resStyles.warningScan : resStyles.invalidScan;
              const badgeClass = isScanValid ? resStyles.validBadge : resStyles.invalidBadge;

              return (
                <div key={idx} className={`${resStyles.historyScanCard} ${cardClass}`}>
                  <div className={resStyles.historyScanCardHeader}>
                    <div className={resStyles.historyScanTime}>
                      <i className="ri-calendar-line"></i>
                      <span>{dateStr}</span>
                      <span className="mx-1 text-muted">•</span>
                      <i className="ri-time-line"></i>
                      <span>{timeStr}</span>
                    </div>
                    {isScanSkipped ? (
                      <span
                        className="badge bg-warning bg-opacity-10 border border-warning border-opacity-50 px-2 py-1 text-uppercase fw-bold shadow-sm"
                        style={{
                          fontSize: "11px",
                          backgroundColor: "#fff8e6",
                          color: "#b45309",
                          borderColor: "#f59e0b",
                          borderRadius: "6px",
                          padding: "3px 8px",
                        }}
                      >
                        SKIPPED
                      </span>
                    ) : (
                      <span className={`${resStyles.historyStatusBadge} ${badgeClass}`}>
                        {row.result || "valid"}
                      </span>
                    )}
                  </div>

                  <div className={resStyles.historyScanCardBody}>
                    {(activeTab === "step1" || row.subjectbarcode) && (
                      <div className={resStyles.historyScanFieldRow}>
                        <span className={resStyles.historyScanFieldLabel}>
                          <i className="ri-user-3-line"></i> Subject Barcode:
                        </span>
                        <span className={resStyles.historyScanFieldValue}>
                          {row.subjectbarcode || "-"}
                        </span>
                      </div>
                    )}

                    <div className={resStyles.historyScanFieldRow}>
                      <span className={resStyles.historyScanFieldLabel}>
                        <i className="ri-bubble-chart-line"></i> Vacutainer Barcode:
                      </span>
                      <span className={resStyles.historyScanFieldValue}>
                        {row.vaccutainerbarcode || "-"}
                      </span>
                    </div>

                    {activeTab === "step1" && (
                      <>
                        {row.dosingscheduletime && (
                          <div className={resStyles.historyScanFieldRow}>
                            <span className={resStyles.historyScanFieldLabel}>
                              <i className="ri-time-line"></i> Schedule Time:
                            </span>
                            <span className={resStyles.historyScanFieldValue}>
                              {formatTime24Hour(row.dosingscheduletime)}
                            </span>
                          </div>
                        )}
                        {row.samplecollectiontime && (
                          <div className={resStyles.historyScanFieldRow}>
                            <span className={resStyles.historyScanFieldLabel}>
                              <i className="ri-drop-line"></i> Collected Time:
                            </span>
                            <span className={resStyles.historyScanFieldValue}>
                              {formatTime24Hour(row.samplecollectiontime)}
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    {(activeTab === "step2" || row.aliquotbarcode) && (
                      <div className={resStyles.historyScanFieldRow}>
                        <span className={resStyles.historyScanFieldLabel}>
                          <i className="ri-test-tube-line"></i> Aliquot Barcode:
                        </span>
                        <span className={resStyles.historyScanFieldValue}>
                          {row.aliquotbarcode || "-"}
                        </span>
                      </div>
                    )}

                    {row.resultmessage && (
                      <div className={`${resStyles.historyResultMsgBanner} ${isScanValid ? resStyles.validMsg : resStyles.invalidMsg}`}>
                        <i className={isScanValid ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"}></i>
                        <span>{row.resultmessage}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileScanningScansOverlay;
